import type { LucideIcon } from 'lucide-react'

/**
 * Nav registry (OVERVIEW §3): each feature contributes one entry from its
 * `nav.ts`; the shell renders whatever is registered — the shell never
 * hardcodes feature knowledge. Permission + module-toggle gating applied at
 * render time (D22).
 */
export interface NavEntry {
  label: string
  icon: LucideIcon
  path: string
  /** Permission string required to see this entry (undefined = always). */
  permission?: string
  /** Module registry key — hidden entirely when the module is disabled. */
  moduleKey?: string
  /** Sort order within the sidebar; lower first. */
  order: number
}

const entries: NavEntry[] = []

export function registerNav(entry: NavEntry, moduleKey?: string): void {
  if (entries.some((e) => e.path === entry.path)) return
  entries.push(moduleKey && !entry.moduleKey ? { ...entry, moduleKey } : entry)
  entries.sort((a, b) => a.order - b.order)
}

export function getNavEntries(): readonly NavEntry[] {
  return entries
}

// ── Grouped IA (owner taxonomy 2026-07-17, item 61) ──────────────────────
// The grouping is a CENTRAL table keyed by path so features stay ignorant
// of shell layout; gated modules simply appear in their group when enabled.

export interface NavGroup {
  key: string
  label: string
}

export const NAV_GROUPS: readonly NavGroup[] = [
  // FIRST by owner decision (2026-08-14): "My Tasks" is the queue an operator
  // opens to find out what to do today, so it sits above the domain groups.
  { key: 'my-tasks', label: 'My Tasks' },
  { key: 'quality', label: 'Quality' },
  { key: 'maintenance', label: 'Maintenance' },
  { key: 'hr', label: 'Human Resources' },
  { key: 'facilities', label: 'Facilities' },
  { key: 'metrics', label: 'Metrics' },
  { key: 'settings', label: 'Settings' },
]

const GROUP_BY_PATH: Record<string, string> = {
  '/checks': 'quality',
  '/cars': 'quality',
  '/audits': 'quality',
  '/suppliers/quality': 'quality',
  '/quality/changes': 'quality',
  '/quality/deviations': 'quality',
  '/quality/oos': 'quality',
  '/quality/lots': 'quality',
  '/quality/nonconformances': 'quality',
  '/quality/esign': 'quality',
  '/quality/risks': 'quality',
  '/quality/fmea': 'quality',
  '/inspections': 'maintenance',
  '/maintenance': 'maintenance',
  '/equipment': 'maintenance',
  '/downtime': 'maintenance',
  '/telemetry-rules': 'maintenance',
  '/parts': 'maintenance',
  '/vendors': 'maintenance',
  '/workers': 'hr',
  '/safety': 'hr',
  '/help-requests': 'hr',
  '/training': 'hr',
  '/attendance': 'hr',
  '/roster': 'hr',
  '/documents': 'hr',
  '/people/hr-records': 'hr',
  /*
   * Assignment is NOT Human Resources (08-12 audit E2).
   *
   * `'/assignment': 'hr'` used to live on this line, from a 2026-07-17 owner
   * decision that call-ins and coverage sat beside Roster and Attendance. The
   * audit's finding is that it put DISPATCHING WORK in the HR menu: a
   * supervisor looking for "who is doing this work order" went to Human
   * Resources, and the one queue mixed checks with maintenance.
   *
   * The work now lives with the work — check assignment under Quality beside
   * Checks, maintenance dispatch under Maintenance beside Work orders — and
   * the cross-domain view is Facilities-grouped like the other operational
   * consoles. `/assignment` itself has no entry; its route redirects.
   */
  '/checks/assignment': 'quality',
  '/maintenance/dispatch': 'maintenance',
  /*
   * These two are CROSS-DOMAIN and are mapped explicitly rather than left to
   * the unknown-path fallback, so the choice is a decision somebody made and
   * not an accident of ordering. The personal queue and the activity workspace
   * both span checks, maintenance and corrective actions, so neither belongs
   * under Quality or Maintenance.
   *
   * They lived under `facilities` until 2026-08-14 because that was the
   * cross-cutting bucket, and "Facilities → Available work" reads as a
   * building, not a to-do list. OWNER DECISION 2026-08-14: they get their own
   * group, `my-tasks` / "My Tasks", placed FIRST — see `NAV_GROUPS` above.
   */
  '/my-assignments': 'my-tasks',
  '/my-work': 'my-tasks',
  // Owner decision 2026-09-18: the cross-team decision log is a supervisor's
  // oversight view, not the signed-in person's work — it sits with Reports.
  '/assignment/activity': 'metrics',
  '/locations': 'facilities',
  '/organization': 'facilities',
  '/chat': 'facilities',
  '/reports': 'metrics',
  '/ai': 'metrics',
  // M-02: administrative surfaces (integrations, modules, search settings,
  // governance, automation, change-control settings) are Settings-hub cards
  // only — no sidebar entries, so no grouping rows. Forms stays: it is a
  // content-authoring surface for quality leads, not company configuration.
  '/forms': 'settings',
  '/settings': 'settings',
}

/** Entries bucketed into the ordered groups; unknown paths land in the
 * last-resort Facilities bucket so nothing silently disappears. */
export function groupNavEntries(
  visible: readonly NavEntry[],
): { group: NavGroup; items: NavEntry[] }[] {
  return NAV_GROUPS.map((group) => ({
    group,
    items: visible.filter(
      (entry) => (GROUP_BY_PATH[entry.path] ?? 'facilities') === group.key,
    ),
  })).filter(({ items }) => items.length > 0)
}
